import { takeEvery, call, put } from 'redux-saga/effects';
import actionTypes from 'redux/actionTypes';
import {
	getReportsListSuccess,
	getReportsListFailure,
	getSharedReportsListSuccess,
	getSharedReportsListFailure,
	downloadReportSuccess,
	downloadReportFailure,
	downloadLargeReportFailure,
	downloadLargeReportSuccess,
	getSharedReportsList,
	deleteReportSuccess,
	deleteReportFailure,
	refreshReportList,
	refreshReportListSuccess,
	refreshReportListFailure,
	initiateRunNowSuccess,
	initiateRunNowFailure,
	getUserListSuccess,
	getUserListFailure,
	setUsersWithReportAccessSuccess,
	setGrantUserAccessFailure,
	removeReportAccessFailure,
	resetUsersWithReportAccess,
	leaveReportSuccess,
	leaveReportFailure,
	merchantUserListSuccess,
	dvUserListSuccess,
	merchantUserListFailure,
	dvUserListFailure,
	getSavedReportsListSuccess,
	getSavedReportsListFailure,
	setReportListFilterValues,
	getReportStartAndEndDatesSuccess,
	getReportStartAndEndDatesFailure
} from './Dashboard.actions';
import APIURLPATH from 'constants/apiconstants';
import { getData } from 'service/getData.service';
import { postData, postDownload } from 'service/postData.service';
import { CSV_FILE_TYPE, MAX_ROW_COUNT } from 'constants/constant';
import { getListFilterValues } from 'helper/helper';
import { getErrorCode, BLOCKED_STATUS_CODE } from 'helper/perimeterX';
import { logUIError } from 'helper/logging';

const delay = (time) => new Promise((resolve) => setTimeout(resolve, time));

export function* getReportsListSaga(request) {
	let viewResponse;
	try {
		const viewApi = APIURLPATH.REPORT_VIEW;

		viewResponse = yield call(getData, viewApi, request.payload);

		if (viewResponse?.statusCode >= 400) {
			throw new Error(viewResponse?.statusCode);
		}

		const reportNameList = viewResponse?.json.map((el) => el.reportName.toLowerCase());
		const response = {
			reportsList: viewResponse?.json,
			reportNameList: reportNameList
		};
		yield put(setReportListFilterValues(getListFilterValues(viewResponse?.json)));
		yield put(getReportsListSuccess(response));
	} catch (error) {
		logUIError(
			request,
			actionTypes.GET_REPORTS_LIST,
			APIURLPATH.REPORT_VIEW,
			JSON.stringify(request.payload),
			viewResponse ? JSON.stringify(viewResponse) : 'No error response'
		);
		yield put(getReportsListFailure(505));
	}
}

export function* getSharedReportsListSaga(request) {
	let sharedResponse;
	try {
		const sharedApi = APIURLPATH.REPORT_SHARED_WITH_ME;
		sharedResponse = yield call(getData, sharedApi, request.payload);

		if (sharedResponse?.statusCode >= 400) {
			throw new Error(sharedResponse?.statusCode);
		}
		const response = { sharedList: sharedResponse?.json };

		yield put(getSharedReportsListSuccess(response));
	} catch (error) {
		logUIError(
			request,
			actionTypes.GET_SHARED_REPORTS_LIST,
			APIURLPATH.REPORT_SHARED_WITH_ME,
			JSON.stringify(request.payload),
			sharedResponse ? JSON.stringify(sharedResponse) : 'No error response'
		);
		yield put(getSharedReportsListFailure(505));
	}
}

export function* downloadReportSaga(request) {
	const { companyName, requestBody, rowCount, fileType } = request.payload;
	let response;
	try {
		response = yield call(postDownload, APIURLPATH.DOWNLOAD_FILE, {
			requestBody,
			companyName
		});
		if (response?.statusCode >= 400) {
			throw new Error(getErrorCode(response));
		} else {
			yield put(downloadReportSuccess());
		}

		if (rowCount > MAX_ROW_COUNT && fileType.toUpperCase() === CSV_FILE_TYPE) {
			yield call(delay, 5000);
			yield put(downloadLargeReportSuccess());
		}
	} catch (error) {
		logUIError(
			request,
			actionTypes.DOWNLOAD_REPORT,
			APIURLPATH.DOWNLOAD_FILE,
			JSON.stringify(request.payload),
			response ? JSON.stringify(response) : 'No error response'
		);
		yield put(downloadReportFailure(error?.message));
		if (error?.message != BLOCKED_STATUS_CODE) {
			if (rowCount > MAX_ROW_COUNT && fileType.toUpperCase() === CSV_FILE_TYPE) {
				yield call(delay, 5000);
				yield put(downloadLargeReportFailure());
			}
		}
	}
}

export function* deleteReportSaga(request) {
	try {
		const response = yield call(postData, APIURLPATH.DELETE_REPORT, request.payload);

		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}

		yield put(deleteReportSuccess(response?.json));
	} catch (error) {
		yield put(deleteReportFailure(error));
	}
}

export function* refreshReportListSaga(request) {
	try {
		const response = yield call(getData, APIURLPATH.REPORT_VIEW, request.payload);

		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}

		yield put(refreshReportListSuccess(response?.json));
	} catch (error) {
		yield put(refreshReportListFailure(505));
	}
}

export function* initiateRunNowSaga(request) {
	const { requestBody } = request.payload;
	try {
		const response = yield call(getData, `${APIURLPATH.RUN_NOW}${requestBody}`, request.payload);

		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}

		yield put(initiateRunNowSuccess(response?.json));
	} catch (error) {
		yield put(initiateRunNowFailure(505));
	}
}

export function* getUserListSaga(request) {
	let response;
	try {
		const {
			payload: { search = '' }
		} = request;

		const url = `${APIURLPATH.SUUPLIER_USER_SEARCH}${search}&pageNo=0&pageSize=50`;

		response = yield call(getData, url, request.payload);

		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}
		const data = response?.json?.result?.content;

		yield put(getUserListSuccess(data));
	} catch (error) {
		logUIError(
			request,
			actionTypes.GET_USER_LIST,
			APIURLPATH.SUUPLIER_USER_SEARCH,
			JSON.stringify(request.payload),
			response ? JSON.stringify(response) : 'No error response'
		);
		yield put(getUserListFailure(505));
	}
}

export function* grantUserAccessSaga(request) {
	try {
		const { reportId, loginId, companyName } = request.payload;
		const response = yield call(postData, `${APIURLPATH.REPORT_SHARE}${reportId}`, request.payload);
		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}
		yield put(resetUsersWithReportAccess());
		yield put(setUsersWithReportAccessSuccess(response?.json?.sharedWith));
		yield put(
			refreshReportList({
				requestBody: loginId,
				companyName: companyName
			})
		);
	} catch (error) {
		yield put(setGrantUserAccessFailure(505));
	}
}

export function* removeReportAccessSaga(request) {
	try {
		const { reportId, loginId, companyName } = request.payload;
		const response = yield call(
			postData,
			`${APIURLPATH.REPORT_UNSHARED}${reportId}`,
			request.payload
		);
		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}
		yield put(resetUsersWithReportAccess());
		yield put(setUsersWithReportAccessSuccess(response?.json?.sharedWith));
		yield put(
			refreshReportList({
				requestBody: loginId,
				companyName: companyName
			})
		);
	} catch (error) {
		yield put(removeReportAccessFailure(505));
	}
}

export function* leaveReportSaga(request) {
	try {
		const { reportId, loginId, companyName } = request.payload;
		const response = yield call(postData, `${APIURLPATH.LEAVE_REPORT}${reportId}`, request.payload);
		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}
		yield put(leaveReportSuccess(response));
		yield put(
			getSharedReportsList({
				requestBody: loginId,
				companyName: companyName
			})
		);
	} catch (error) {
		yield put(leaveReportFailure(505));
	}
}

export function* getMerchantUserListSaga(request) {
	try {
		const {
			payload: { search = '' }
		} = request;

		const url = `${APIURLPATH.MERCHANT_USER_SEARCH}${search}&pageNo=0&pageSize=50`;

		const response = yield call(getData, url, request.payload);

		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}
		const data = response?.json?.result?.content;
		yield put(merchantUserListSuccess(data));
	} catch (error) {
		yield put(merchantUserListFailure(505));
	}
}

export function* getDVUserListSaga(request) {
	try {
		const {
			payload: { search = '' }
		} = request;

		const url = `${APIURLPATH.DV_USER_SEARCH}${search}&pageNo=0&pageSize=50`;

		const response = yield call(getData, url, request.payload);

		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}
		const data = response?.json?.result?.content;
		const formattedData = [];
		data.forEach((item) => {
			const obj = {
				email: item.email,
				firstName: item.first_name,
				lastName: item.last_name,
				loginId: item.login_id
			};
			formattedData.push(obj);
		});
		yield put(dvUserListSuccess(formattedData));
	} catch (error) {
		yield put(dvUserListFailure(505));
	}
}

export function* getSavedReportsListSage(request) {
	try {
		const url = APIURLPATH.ALL_DRAFTS;

		let response = yield call(getData, url, request.payload);

		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}
		// Saving Saved report template Names in Store
		const savedReports = response?.json;
		const savedReportDataset = [];
		savedReports.forEach((report) => {
			if (!savedReportDataset.includes(report.templateName)) {
				savedReportDataset.push(report.templateName);
			}
		});
		savedReportDataset.sort();
		savedReportDataset.unshift('All');
		yield put(setReportListFilterValues({ savedReportDataset }));

		yield put(getSavedReportsListSuccess(savedReports));
	} catch (error) {
		yield put(getSavedReportsListFailure(505));
	}
}

export function* getStartAndEndDatesSaga(request) {
	try {
		const url = APIURLPATH.CALENDER_ROLL_DIM;
		const response = yield call(getData, url, request.payload);

		if (response?.statusCode >= 400) {
			throw new Error(response?.statusCode);
		}
		yield put(getReportStartAndEndDatesSuccess(response?.json));
	} catch (error) {
		yield put(getReportStartAndEndDatesFailure());
	}
}

export function* dashboardSagaWatcher() {
	yield takeEvery(actionTypes.GET_REPORTS_LIST, getReportsListSaga);
	yield takeEvery(actionTypes.GET_SHARED_REPORTS_LIST, getSharedReportsListSaga);
	yield takeEvery(actionTypes.DOWNLOAD_REPORT, downloadReportSaga);
	yield takeEvery(actionTypes.DELETE_REPORT, deleteReportSaga);
	yield takeEvery(actionTypes.REFRESH_REPORTS_LIST, refreshReportListSaga);
	yield takeEvery(actionTypes.INITIATE_RUN_NOW, initiateRunNowSaga);
	yield takeEvery(actionTypes.GET_USER_LIST, getUserListSaga);
	yield takeEvery(actionTypes.SET_USERS_WITH_REPORT_ACCESS, grantUserAccessSaga);
	yield takeEvery(actionTypes.REMOVE_REPORT_ACCESS, removeReportAccessSaga);
	yield takeEvery(actionTypes.LEAVE_REPORT, leaveReportSaga);
	yield takeEvery(actionTypes.GET_MERCHANT_USER_LIST, getMerchantUserListSaga);
	yield takeEvery(actionTypes.GET_DV_USER_LIST, getDVUserListSaga);
	yield takeEvery(actionTypes.GET_SAVED_REPORTS_LIST, getSavedReportsListSage);
	yield takeEvery(actionTypes.GET_START_AND_END_DATES, getStartAndEndDatesSaga);
}
